import { Inject } from '@odinjs/odin';
import MyChartFileListItemCardAPI from '@philips-emr/patient-components/src/components/my-chart-file-list-item-card/my-chart-file-list-item-card-api';
import { AutocompleteAPI, DeckAPI, DeckValue, FetchAPI, FilterAPI, ModalButton, ModalProps, ModalService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnAfterFetch, OnClick, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';
import { FragmentNames, MyChartSendingDocumentsAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { MyChartSendingDocumentsOtherData, MyChartSendingDocumentsType } from '../../constants/my-chart-sending-documents-type';
import PacsLinkGeneratorService from '../../services/pacs-link-generator-service';
import { MyChartUtils } from '../../utils/my-chart-utils';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import './my-chart-sending-documents-controller.css';

interface DeckDocumentlItemProps {
  dicomAccessKey: string;
  documentRead: string;
  documentType: string;
  localDelivery: string;
  nrSequence: number;
  path: string;
  savedAt: string;
  sequenceNumber: number;
  status: string;
}

@Controller({ fragment: FragmentNames.MY_CHART_SENDING_DOCUMENTS })
export default class MyChartSendingDocumentsController extends BaseController {

  fetchesAPI: Map<MyChartSendingDocumentsType, FetchAPI> = new Map();
  decksAPI: Map<MyChartSendingDocumentsType, DeckAPI> = new Map();
  pacsLinkGeneratorService: PacsLinkGeneratorService = new PacsLinkGeneratorService();

  @Inject(MyChartSendingDocumentsAliases.AUTOCOMPLETE_DOCUMENT_TYPE)
    autocompleteApi!: AutocompleteAPI;

  @Inject(MyChartSendingDocumentsAliases.FETCH_RECEIVED)
    fetchReceivedApi!: FetchAPI;

  @Inject(MyChartSendingDocumentsAliases.FETCH_SENT)
    fetchSentApi!: FetchAPI;

  @Inject(MyChartSendingDocumentsAliases.FILTER_SENDING_DOCUMENTS)
    filterApi!: FilterAPI;

  @OnClick({ target: MyChartSendingDocumentsAliases.BTN_SEND_NEW_DOCUMENT })
  handleBtnSendNewDocument(): void {
    MyChartUtils.pushToSubRoute(APPLICATION_ROUTES.MY_CHART_NEW_DOCUMENT);
  }

  @OnMount({ target: [
    MyChartSendingDocumentsAliases.FETCH_RECEIVED,
    MyChartSendingDocumentsAliases.FETCH_SENT
  ] })
  handleFetchsLoading(event: MountEvent): void {
    const fetchAPI = event.target as FetchAPI;
    this.filterApi.clear();
    fetchAPI.setParameters({ patientId: UserContext.patientId });
    fetchAPI.forceFetch();
  }

  @OnMount({ target: [
    MyChartSendingDocumentsAliases.DECK_RECEIVED,
    MyChartSendingDocumentsAliases.DECK_SENT
  ] })
  handleDocumentDecksLoading(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;

    let type;
    switch(deckAPI.getAlias()) {
      case MyChartSendingDocumentsAliases.DECK_RECEIVED:
        type = MyChartSendingDocumentsType.RECEIVED;
        break;
      case MyChartSendingDocumentsAliases.DECK_SENT:
        type = MyChartSendingDocumentsType.SENT;
        break;
    }

    if(type != null) {
      this.context.set({tabName: type});
      this.decksAPI.set(type, deckAPI);
    }
  }

  @OnMount({ target: [
    MyChartSendingDocumentsAliases.MY_CHART_FILE_LIST_ITEM_CARD_RECEIVED,
    MyChartSendingDocumentsAliases.MY_CHART_FILE_LIST_ITEM_CARD_SENT
  ] })
  handleOnClickPacsButton(event: ClickEvent): void {
    const myChartItem = event.target as MyChartFileListItemCardAPI;
    myChartItem.setOnClickPacs(this.handlePacsButton);
  }

  private handlePacsButton = (dicomAccessKey: string): void => {
    const dialogButtons: ModalButton[] = [
      {
        action: () => undefined,
        title: MessageService.translate('patient.cancel'),
        type: ModalButtonType.CANCEL,
      },
      {
        action: () => { this.callPacsInChrome(dicomAccessKey); },
        title: MessageService.translate('patient.continue'),
        type: ModalButtonType.SUCCESS,
      }
    ];

    const dialogProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('patient.pacs.open-new-tab'),
      dockButtons: dialogButtons,
      focusOnOpen: true,
      title: MessageService.translate('patient.exam-details'),
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogProps);
  }

  private callPacsInChrome (dicomAccessKey: string): void {
    const { patientId } = UserContext;
    const windowReference = window.open();
    this.pacsLinkGeneratorService.getPacsState()
      .then(pacsState => {
        if (!pacsState.enabled) {
          showToastNotificationError(MessageService.translate('patient.no-pacs-available'));
          return;
        }

        this.pacsLinkGeneratorService.openPacsInChrome({
          examId: dicomAccessKey || '',
          organizationId: '',
          patientId: patientId.toString(),
        }, windowReference);
      }).catch(() => {
        showToastNotificationError(MessageService.translate('patient.report-error'));
      });
  }

  @OnClick({
    target: [
      MyChartSendingDocumentsAliases.MY_CHART_FILE_LIST_ITEM_CARD_RECEIVED,
      MyChartSendingDocumentsAliases.MY_CHART_FILE_LIST_ITEM_CARD_SENT
    ]
  })
  handleClickDocumentCard(event: ClickEvent): void {
    const item = event.value as DeckDocumentlItemProps;

    ModalService.openDialog({
      className: isMobile() ? '' : 'my-chart-sending-documents__dialog-modal',
      contextData: { filePath: item.path },
      fragment: 'my-chart-view-sent-document',
      onClose: () => this.markDocumentAsRead(item),
    });
  }

  private markDocumentAsRead(document: DeckDocumentlItemProps): void {
    if (document.documentRead === 'S') {
      return;
    }

    const bodyContent = {
      documentType: document.localDelivery,
      nrSequence: document.sequenceNumber,
    }

    HttpService.put(
      APPLICATION_ENDPOINTS.PUT_MC_READ_DOCUMENT,
      { patientId: UserContext.patientId },
      { body: bodyContent }
    ).then(() => {
      this.updateItemOnDeck(document);
    });
  }

  private updateItemOnDeck(item: DeckDocumentlItemProps): void {
    const deckAPI = this.decksAPI.get(item.localDelivery as MyChartSendingDocumentsType);
    if (!deckAPI) {
      return;
    }

    const currentDeckData = deckAPI.getData() as unknown as DeckDocumentlItemProps[];
    const itemIndex = currentDeckData.findIndex(document => document.sequenceNumber === item.sequenceNumber);
    if (itemIndex === -1) {
      return;
    }

    currentDeckData[itemIndex].documentRead = 'S';
    deckAPI.setData(currentDeckData as unknown as DeckValue[]);
  }

  @OnAfterFetch(MyChartSendingDocumentsAliases.FETCH_DOCUMENTS_TYPE_LIST)
  handleAfterDocumentTypeListFetch(event: FetchEvent): void {
    const documentData = event.data as DeckDocumentlItemProps[];
    const newDocumentData = documentData.map(item => {
      return {
        'documentType': MessageService.translate(item.documentType),
        'value': item.documentType
      }
    });
    this.autocompleteApi.setData(newDocumentData);
  }

  @OnSubmit({ target: MyChartSendingDocumentsAliases.FILTER_SENDING_DOCUMENTS })
  onSubmitFilter(response: SubmitEvent): void {
    const data = response.record.documentType as DeckDocumentlItemProps;
    const type = this.context.get(MyChartSendingDocumentsOtherData.TAB_NAME) as string;

    const fetch = type === MyChartSendingDocumentsType.RECEIVED ? this.fetchReceivedApi : this.fetchSentApi;
    if (data && type === MyChartSendingDocumentsType.RECEIVED) {
      fetch.setParameters({documentType: data.documentType, patientId: UserContext.patientId});
    } else {
      fetch.setParameters({patientId: UserContext.patientId});
    }
    fetch.forceFetch();
  }
}